/**
 * Wave 29 Chunk 4 — Leaderboard card.
 *
 * Renders a leaderboard payload from /api/team/leaderboard. Designed for
 * embedding both on the Performance hub and inside other pages (e.g.,
 * dashboard widgets).
 */
import { Link } from "react-router-dom";
import { Trophy, ChevronRight } from "lucide-react";

// Bug 1 fix — Dirk 2026-05-13. Per-metric value formatting so 240 min
// shows as "4h 0m" and avg-delta-to-07:00 shows as "+12m late" / "-8m early".
function formatValue(v, fmt, suffix) {
  if (v == null || v === "") return "—";
  if (fmt === "minutes_to_hours") {
    const n = Number(v) || 0;
    const h = Math.floor(n / 60);
    const m = n % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  }
  if (fmt === "minutes_signed") {
    const n = Math.round(Number(v) || 0);
    if (n === 0) return "on time";
    if (n < 0) return `${Math.abs(n)}m early`;
    return `+${n}m late`;
  }
  return `${v}${suffix || ""}`;
}

const RANK_STYLES = {
  1: "bg-yellow-300/[0.12] border-yellow-300/40 text-yellow-200",
  2: "bg-slate-300/[0.08] border-slate-300/40 text-slate-200",
  3: "bg-amber-700/[0.10] border-amber-700/40 text-amber-300",
};

export default function LeaderboardCard({ data, myUserId, dense = false }) {
  if (!data) return <div className="h-32 hv-card animate-pulse" />;
  const items = data.items || [];
  return (
    <div className="hv-card p-4" data-testid="leaderboard-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] inline-flex items-center gap-1">
            <Trophy className="w-3 h-3" /> Leaderboard
          </div>
          <h2 className="font-display text-lg sm:text-xl font-bold mt-0.5">{data.metric_label}</h2>
          {!dense && data.metric_description && (
            <p className="text-[11px] text-[#a0aabf] mt-0.5">{data.metric_description}</p>
          )}
        </div>
        {data.my_rank != null && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087]">Your rank</div>
            <div className="text-2xl font-display font-bold tabular-nums text-[#1FB6C1]">#{data.my_rank}</div>
            <div className="text-[10px] text-[#a0aabf]">of {data.total}</div>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-xs text-[#647087] text-center py-6">No data yet for this window.</div>
      ) : (
        <ol className="space-y-1.5" data-testid="leaderboard-list">
          {items.map((r) => {
            const isMe = r.user_id === myUserId;
            const rs = RANK_STYLES[r.rank] || "bg-white/[0.03] border-white/10";
            return (
              <li
                key={r.user_id}
                className={`flex items-center gap-3 p-2 rounded-lg border ${rs} ${isMe ? "ring-1 ring-[#1FB6C1]" : ""}`}
                data-testid={`leaderboard-row-${r.rank}`}
              >
                <div className="w-7 text-center font-display font-bold tabular-nums text-sm">#{r.rank}</div>
                <div className="w-8 h-8 rounded-full bg-white/5 overflow-hidden flex items-center justify-center shrink-0">
                  {r.avatar_url ? (
                    <img src={r.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-[9px] text-[#a0aabf] font-semibold">{(r.name || "?").slice(0, 2).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate flex items-center gap-1.5">
                    {r.name}
                    {isMe && <span className="text-[9px] uppercase tracking-wider text-[#1FB6C1]">you</span>}
                  </div>
                  <div className="text-[10px] text-[#647087] capitalize">{r.role}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display text-lg font-bold tabular-nums">
                    {formatValue(r.value, r.format_value, r.suffix)}
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
      {!dense && (
        <div className="mt-3 pt-3 border-t border-white/5 flex justify-end">
          <Link to="/team/performance" className="text-[11px] text-[#1FB6C1] inline-flex items-center gap-0.5 hover:underline">
            Full performance <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
